import PropTypes from 'prop-types';
import { ArrayField, FunctionField, SelectField, SingleFieldList, TextField, usePermissions, useRecordContext, useTranslate } from 'react-admin';
import { Chip } from '@mui/material';
import { DetailsShow } from '@/components/global/drawers/DetailsShow.jsx';
import { DetailsActionsPopover } from '@/components/global/buttons/DetailsActionsPopover.jsx';
import CustomEditModalButton from '@/components/global/buttons/CustomEditModalButton.jsx';
import CustomDeleteButton from '@/components/global/buttons/CustomDeleteButton.jsx';
import { CustomBooleanField } from '@/components/global/fields/CustomBooleanField.jsx';
import { DrawerLink } from '@/components/global/drawers/DrawerLink.jsx';
import { useDrawer } from '@/composables/useDrawer.js';
import { canViewDetails } from '@/helpers/permissions/canViewDetails.js';
import { aliasToString } from '@/helpers/device/softwareHelpers.js';
import { deploymentStatusShortChoices, softwareTypeChoices } from '@/components/global/options/selectOptions.jsx';

const SoftwareUpdateRuleDetailsActions = ({ handleClose }) => {
    const record = useRecordContext();
    const translate = useTranslate();
    const { permissions } = usePermissions();
    const { openDrawer } = useDrawer();

    if (!permissions?.isAdmin || !record?.id) {
        return null;
    }

    return (
        <DetailsActionsPopover>
            <CustomEditModalButton
                label={translate('resources.softwareUpdateRule.labels.edit')}
                onOpen={() => openDrawer(record.id, 'software-update-rule-edit')}
            />
            <CustomDeleteButton
                resource='software-update-rule'
                id={String(record.id)}
                label={translate('resources.softwareUpdateRule.labels.delete')}
                onSuccess={handleClose}
            />
        </DetailsActionsPopover>
    );
};

SoftwareUpdateRuleDetailsActions.propTypes = {
    handleClose: PropTypes.func,
};

const StatusChip = () => {
    const record = useRecordContext();
    const translate = useTranslate();
    const statusName = deploymentStatusShortChoices(translate).find(choice => choice.id === record)?.name || record;

    return <Chip label={statusName} />;
};

export const SoftwareUpdateRuleShow = ({ handleClose, elementId }) => {
    const translate = useTranslate();

    return (
        <DetailsShow
            handleClose={handleClose}
            elementId={elementId}
            resource='software-update-rule'
            canView={permissions => canViewDetails('software-update-rule', permissions)}
            actions={<SoftwareUpdateRuleDetailsActions handleClose={handleClose} />}
            showChangeHistory
            historyFieldsSource='resources.softwareUpdateRule.fields'
        >
            <TextField source='name' label={translate('resources.softwareUpdateRule.fields.name')} />
            <TextField source='pattern' label={translate('resources.softwareUpdateRule.fields.pattern')} />
            <ArrayField source='allowedStatuses' label={translate('resources.softwareUpdateRule.fields.allowedStatuses')}>
                <SingleFieldList linkType={false}>
                    <StatusChip />
                </SingleFieldList>
            </ArrayField>
            <SelectField source='alias.software.type' choices={softwareTypeChoices} label={translate('resources.softwareUpdateRule.fields.type')} />
            <FunctionField
                label={translate('resources.softwareUpdateRule.fields.alias')}
                render={record =>
                    record?.alias?.softwareId ? (
                        <DrawerLink resource='software' action='show' source={record.alias.softwareId} label={aliasToString(record.alias)} relative />
                    ) : null
                }
            />
            <CustomBooleanField source='isActive' label={translate('resources.softwareUpdateRule.fields.isActive')} />
        </DetailsShow>
    );
};

SoftwareUpdateRuleShow.propTypes = {
    handleClose: PropTypes.func.isRequired,
    elementId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
